import { Controller } from "@hotwired/stimulus";
import { countries } from "components/common/countries";

// Replaces the jQuery-built country/phone-code picker in
// step2/_booking_form.html.erb (a custom `.select-box`/`.selected-option`
// dropdown, not a native <select>). Reuses the same full country/dial-code/
// flag-image dataset the old picker did (components/common/countries.js) --
// an earlier version of this controller hand-picked a handful of countries
// with guessed flag numbers, which 404'd (only Sweden's 135 was ever
// actually verified). Default country is derived from the current domain's
// TLD, same as the old inline script, falling back to Sweden. Builds
// `you[mobile]` on submit by combining the selected dial code with the
// local number (leading zeros stripped) -- same contract
// step2_submit_controller.js's serializeForm() call expects.
//
// Simplification vs. the old picker: country names in the search list are
// plain English (countries.js has no locale variants built in), not
// localized per gate -- the old version pulled localized_countries.js for
// that. Searching by name still works, just not in the visitor's language.
export default class extends Controller {
  static targets = ["input", "hiddenMobile", "flag", "dialCode", "options", "list", "search"];

  connect() {
    this.selected = this.defaultCountry();
    this.renderList(countries);
    this.applySelection();
    this.boundClose = this.closeOnOutsideClick.bind(this);
    document.addEventListener("click", this.boundClose);
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose);
  }

  defaultCountry() {
    const tld = window.location.hostname.split(".").pop().toUpperCase();
    return countries.find((c) => c.code === tld) || countries.find((c) => c.code === "SE") || countries[0];
  }

  renderList(list) {
    if (!this.hasListTarget) return;
    this.listTarget.innerHTML = list
      .map(
        (c) => `<li><button type="button" class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50" data-code="${c.code}">
        <img src="/assets/flags/${c.flag_code}" class="h-3 w-4" /> <span>${c.phone}</span> <span class="text-gray-500">${c.name}</span>
      </button></li>`
      )
      .join("");
  }

  toggle() {
    this.optionsTarget.classList.toggle("hidden");
  }

  search() {
    const query = this.searchTarget.value.trim().toLowerCase();
    this.renderList(query ? countries.filter((c) => c.name.toLowerCase().includes(query)) : countries);
  }

  select(event) {
    const code = event.target.closest("[data-code]")?.dataset.code;
    const country = countries.find((c) => c.code === code);
    if (!country) return;
    this.selected = country;
    this.applySelection();
    this.optionsTarget.classList.add("hidden");
  }

  applySelection() {
    if (this.hasFlagTarget) this.flagTarget.src = `/assets/flags/${this.selected.flag_code}`;
    if (this.hasDialCodeTarget) this.dialCodeTarget.textContent = this.selected.phone;
  }

  // Called right before the surrounding form submits (see
  // step2_submit_controller.js) to populate the real submitted field.
  writeHiddenField() {
    const local = (this.inputTarget.value || "").replace(/^0+/, "").trim();
    this.hiddenMobileTarget.value = `${this.selected.phone}${local}`;
  }

  closeOnOutsideClick(event) {
    if (this.hasOptionsTarget && !this.element.contains(event.target)) {
      this.optionsTarget.classList.add("hidden");
    }
  }
}
